/**
 * TLE parsing, 3LE file import, and CelesTrak GP API helpers.
 * @see https://celestrak.org/NORAD/documentation/gp-data-formats.php
 */

import * as satellite from 'satellite.js';

export const TLE_LINE_LEN = 69;

export interface TleEntry {
  name: string;
  line1: string;
  line2: string;
}

function tleChecksumDigit(body68: string): string {
  const sum = body68.split('').reduce(
    (a, c) => a + (c === '-' ? 1 : /\d/.test(c) ? +c : 0),
    0,
  );
  return String(sum % 10);
}

/** Pad to 69 chars and fix line checksum (position 69). */
export function normalizeTleLine(raw: string, lineNum: 1 | 2): string {
  const prefix = lineNum === 1 ? '1 ' : '2 ';
  let line = raw.trim();
  if (!line.startsWith(String(lineNum))) {
    line = prefix + line.replace(/^[12]\s*/, '');
  }
  line = line.padEnd(TLE_LINE_LEN, ' ').slice(0, TLE_LINE_LEN);
  const body = line.slice(0, 68);
  return body + tleChecksumDigit(body);
}

/** Normalize both lines; null if prefixes invalid. */
export function normalizeTlePair(l1: string, l2: string): { line1: string; line2: string } | null {
  if (!l1.trim() || !l2.trim()) return null;
  const line1 = normalizeTleLine(l1, 1);
  const line2 = normalizeTleLine(l2, 2);
  if (!line1.startsWith('1 ') || !line2.startsWith('2 ')) return null;
  return { line1, line2 };
}

/** Validate via SGP4 parse (more reliable than checksum-only for imported TLEs). */
export function validateTlePair(l1: string, l2: string): string {
  if (!l1.trim() && !l2.trim()) return '';
  const norm = normalizeTlePair(l1, l2);
  if (!norm) {
    if (l1.trim() && !l1.trim().startsWith('1 ')) return 'Line 1 must start with "1 "';
    if (l2.trim() && !l2.trim().startsWith('2 ')) return 'Line 2 must start with "2 "';
    return 'Enter both TLE lines';
  }
  try {
    const rec = satellite.twoline2satrec(norm.line1, norm.line2);
    if (rec.error !== 0) return 'TLE format rejected by SGP4 propagator';
  } catch {
    return 'Invalid TLE format';
  }
  return '';
}

export function extractCatalogNumber(line1: string): string | null {
  const padded = normalizeTleLine(line1, 1);
  const cat = padded.slice(2, 7).trim();
  return /^\d+$/.test(cat) ? cat : null;
}

function entryFromLines(name: string, raw1: string, raw2: string): TleEntry | null {
  const norm = normalizeTlePair(raw1, raw2);
  if (!norm || validateTlePair(norm.line1, norm.line2)) return null;
  return { name, ...norm };
}

/** Parse 2-line or 3-line (name + lines) TLE text. */
export function parseTleText(text: string): TleEntry | null {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length >= 3 && lines[1].startsWith('1 ')) {
    return entryFromLines(lines[0].trim(), lines[1], lines[2]);
  }
  if (lines.length >= 2 && lines[0].startsWith('1 ')) {
    return entryFromLines('', lines[0], lines[1]);
  }
  return null;
}

/** Parse bulk .tle / .txt / 3LE files (multiple satellites). */
export function parseTleFileContent(text: string): TleEntry[] {
  const lines = text.split('\n').map(l => l.trimEnd());
  const entries: TleEntry[] = [];
  let pendingName = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    if (line.startsWith('1 ') && line.length >= 60) {
      let raw2 = '';
      for (let j = i + 1; j < lines.length; j++) {
        const next = lines[j].trim();
        if (!next) continue;
        if (next.startsWith('2 ')) {
          raw2 = next;
          i = j;
          break;
        }
        if (next.startsWith('1 ')) break;
      }
      if (raw2) {
        const entry = entryFromLines(
          pendingName || `SAT_${extractCatalogNumber(line) ?? entries.length + 1}`,
          line,
          raw2,
        );
        if (entry) entries.push(entry);
        pendingName = '';
      }
      continue;
    }

    if (!line.startsWith('2 ')) {
      pendingName = line;
    }
  }

  return entries;
}

const CELESTRAK_GP = 'https://celestrak.org/NORAD/elements/gp.php';

/** Fetch latest GP TLE from CelesTrak by NORAD catalog number (CATNR). */
export async function fetchCelesTrakTle(catnr: string): Promise<TleEntry | null> {
  const url = `${CELESTRAK_GP}?CATNR=${encodeURIComponent(catnr)}&FORMAT=TLE`;
  const res = await fetch(url, { headers: { Accept: 'text/plain' } });
  if (!res.ok) {
    console.warn('[TLE] CelesTrak HTTP', res.status, catnr);
    return null;
  }
  const text = await res.text();
  const parsed = parseTleText(text);
  if (parsed) {
    return parsed;
  }
  return null;
}

/** Prefer CelesTrak GP when catalog exists; otherwise use entered lines. */
export async function resolveTleForPreview(
  line1: string,
  line2: string,
  name?: string,
): Promise<TleEntry & { source: 'celestrak' | 'local' }> {
  const localNorm = normalizeTlePair(line1, line2);
  const catnr = localNorm ? extractCatalogNumber(localNorm.line1) : null;

  if (catnr) {
    try {
      const remote = await fetchCelesTrakTle(catnr);
      if (remote) {
        return {
          ...remote,
          name: name || remote.name || `NORAD_${catnr}`,
          source: 'celestrak',
        };
      }
    } catch (err) {
      console.warn('[TLE] CelesTrak unavailable, using manual TLE', err);
    }
  }

  if (localNorm) {
    return {
      name: name || '',
      ...localNorm,
      source: 'local',
    };
  }

  return {
    name: name || '',
    line1: normalizeTleLine(line1, 1),
    line2: normalizeTleLine(line2, 2),
    source: 'local',
  };
}
